<template>
  <div class="home">
    <h2>Builds</h2>

    <b-container fluid>
      <!-- User Interface controls -->

      <b-row>
        <b-col md="12" class="my-1">
          <b-form-group label-cols-sm="1" label="Filter" class="mb-0">
            <b-input-group>
              <b-form-input
                v-model="filter.ownWhitelabel"
                placeholder="Filter by whitelabel"
                @keypress.enter="forceFilter"
              />
              <b-form-input
                v-model="filter.ownPlatform"
                placeholder="Filter by platform"
                @keypress.enter="forceFilter"
              />
              <b-form-input
                v-model="filter.ownVersion"
                placeholder="Filter by version"
                @keypress.enter="forceFilter"
              />
              <b-input-group-append>
                <b-button
                  :disabled="
                    !filter.ownWhitelabel &&
                      !filter.ownPlatform &&
                      !filter.ownVersion
                  "
                  @click="
                    filter = {};
                    forceFilter();
                  "
                  >Clear</b-button
                >
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="2" label="Sort" class="mb-0">
            <b-input-group>
              <b-form-select v-model="sortBy" :options="sortOptions">
                <option slot="first" :value="null">-- none --</option>
              </b-form-select>
              <b-form-select
                :disabled="!sortBy"
                v-model="sortDesc"
                slot="append"
              >
                <option :value="false">Asc</option>
                <option :value="true">Desc</option>
              </b-form-select>
            </b-input-group>
          </b-form-group>
        </b-col>

        <b-col md="6" class="my-1">
          <b-form-group label-cols-sm="3" label="Per page" class="mb-0">
            <b-form-select :options="pageOptions" v-model="perPage" />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Main table element -->
      <b-table
        show-empty
        small
        hover
        stacked="md"
        :items="items"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :filter="filter"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        ref="buildsTable"
        class="buildsTable"
      >
        <template v-slot:cell(urls)="row">
          <div v-b-popover.hover="`${row.value}`">
            {{ row.value.slice(0, 40) }}...
          </div>
        </template>

        <template v-slot:cell(isAvailable)="row">
          {{ row.value ? 'Yes' : 'No' }}
        </template>

        <template v-slot:row-details="row">
          <b-card>
            <ul>
              <li v-for="(value, key) in row.item" :key="key">
                {{ key }}: {{ value }}
              </li>
            </ul>
          </b-card>
        </template>

        <template v-slot:cell(actions)="row">
          <b-button
            size="sm"
            class="m-1"
            @click="row.toggleDetails"
            variant="info"
          >
            …
          </b-button>
          <b-button
            size="sm"
            variant="danger"
            class="m-1 ml-3"
            :hidden="row.item.isTester"
            @click="
              deleteBuild({
                target: $event.target,
                build: row.item
              })
            "
          >
            X
          </b-button>
        </template>
      </b-table>

      <b-button
        variant="danger"
        @click="deleteAllBuilds()"
        class="float-right"
        :hidden="this.$store.state.user.role === 'tester'"
        >Delete All Builds</b-button
      >
      <b-button
        variant="danger"
        @click="deleteBuildsByFilter()"
        :hidden="this.$store.state.user.role === 'tester'"
        class="float-right margin-right"
        >Delete By Filter</b-button
      >

      <b-row>
        <b-col md="6" class="my-1">
          <b-pagination
            :total-rows="totalRows"
            :per-page="perPage"
            v-model="currentPage"
            class="my-0"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
/* eslint-disable no-magic-numbers */
const originalForm = {
  id: null,
  urls: null,
  ownWhitelabel: null,
  ownPlatform: null,
  ownVersion: null,
  ownResolution: null,
  isAvailable: null,
  lastCheckedDate: null,
  name: null,
  maxVersion: null
};

export default {
  data() {
    return {
      currentPage: 1,
      perPage: 20,
      pageOptions: [20, 50, 100, 500],
      sortBy: null,
      sortDesc: false,
      filter: { ownWhitelabel: '', ownPlatform: '', ownVersion: '' },
      form: {
        ...originalForm
      },
      isEdit: false
    };
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => {
          return { text: f.label, value: f.key };
        });
    },
    fields() {
      return [
        {
          key: 'id',
          label: 'id',
          sortable: true,
          sortDirection: 'desc'
        },
        {
          key: 'urls',
          label: 'URLs',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'ownWhitelabel',
          label: 'Whitelabel',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'ownPlatform',
          label: 'Platform',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'ownVersion',
          label: 'Version',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'ownResolution',
          label: 'Resolution',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'name',
          label: 'Name',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'isAvailable',
          label: 'Is available',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'lastCheckedDate',
          label: 'Checked at',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'createdAt',
          label: 'Created',
          sortable: true,
          class: 'text-center'
        },
        {
          key: 'updatedAt',
          label: 'Updated',
          sortable: true,
          class: 'text-center'
        },
        { key: 'actions', label: 'Actions' }
      ];
    },
    totalRows() {
      return this.$store.state.builds.count;
    }
  },
  methods: {
    onFiltered() {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.currentPage = 1;
    },
    getCurrentFilter() {
      return Object.keys(this.filter).reduce(
        (acc, curr) =>
          this.filter[curr] ? { ...acc, [curr]: this.filter[curr] } : acc,
        {}
      );
    },
    items(ctx) {
      const filter = this.getCurrentFilter();
      const promise = this.$store.dispatch('getBuilds', {
        ctx,
        filter
      });

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        // Pluck the array of items off our axios response
        const items = this.$store.state.builds.rows.map(comb => {
          if (!comb.isAvailable) {
            comb._rowVariant = 'warning';
          }
          return {
            ...comb,
            isTester: this.$store.state.user.role === 'tester'
          };
        });
        // Must return an array of items or an empty array if an error occurred
        return items || [];
      });
    },
    forceFilter() {
      this.$refs.buildsTable.refresh();
    },
    deleteBuild({ build = null }) {
      if (!build) {
        return;
      }
      const promise = this.$store.dispatch('deleteBuild', build.id);

      // Must return a promise that resolves to an array of items
      return promise.then(() => {
        this.$refs.buildsTable.refresh();
      });
    },
    deleteAllBuilds() {
      if (confirm('Delete all builds?')) {
        const promise = this.$store.dispatch('deleteAllBuilds');

        // Must return a promise that resolves to an array of items
        return promise.then(() => {
          this.$refs.buildsTable.refresh();
        });
      }
    },

    deleteBuildsByFilter() {
      if (
        !this.filter.ownWhitelabel &&
        !this.filter.ownPlatform &&
        !this.filter.ownVersion
      ) {
        alert('Fill some filter input');
        return;
      }

      if (confirm('Delete builds by selecting filter?')) {
        const filter = this.getCurrentFilter();
        const promise = this.$store.dispatch('deleteBuildsByFilter', filter);

        // Must return a promise that resolves to an array of items
        return promise.then(() => {
          this.$refs.buildsTable.refresh();
        });
      }
    }
  }
};
</script>
<style scoped type="text/scss">
.block {
  border-bottom: #dadada 1px solid;
  padding-bottom: 1em;
  margin-bottom: 3em;
}
.container {
  max-width: none;
}
.buildsTable {
  font-size: 0.8rem;
}
.btn {
  font-size: 0.8rem;
}

.margin-right {
  margin-right: 20px;
}

td[data-label='Actions'] {
  white-space: nowrap;
}
</style>
